import { Box, BoxProps, HStack, Link, Text } from "@biblioteksentralen/react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Minus, Plus } from "react-feather";

type Props = {
  children: ReactNode;
  readLessText: string;
  readMoreText: string;
  // can be used to make the content collapsible or not at first render.
  // The way we implemented this runs in the browser, which means first render might differ from the final state.
  isOverflowingGuess?: boolean;
} & BoxProps;

export const CollapsableWithGradient = ({
  children,
  readLessText,
  readMoreText,
  isOverflowingGuess,
  ...boxProps
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(isOverflowingGuess ?? false);
  const readMoreContainerRef = useRef<HTMLDivElement>(null);
  const readMoreContainer = readMoreContainerRef.current;

  useEffect(() => {
    if (readMoreContainer) {
      setIsOverflowing(readMoreContainer.clientHeight < readMoreContainer.scrollHeight);
    }
  }, [readMoreContainer]);

  return (
    <Box {...boxProps} isolation="isolate" marginBottom="-2.5rem">
      <Box position="relative">
        <Text lineClamp={isOpen ? undefined : 4}>{children}</Text>
        {isOverflowing && (
          <Box
            as="span"
            width="100%"
            height="4rem"
            position="absolute"
            bottom="0"
            left="0"
            background="linear-gradient(transparent, white)"
            opacity={isOpen ? 0 : 1}
            transition="0.3s"
            pointerEvents="none"
          />
        )}
      </Box>
      {isOverflowing && (
        <Link
          as="button"
          marginTop="0.25rem"
          marginBottom="3rem"
          zIndex="10"
          onClick={() => setIsOpen(!isOpen)}
          width="fit-content"
          position="relative"
          _focus={undefined}
        >
          <HStack gap="0.25rem">
            {isOpen ? <Minus size="1em" /> : <Plus size="1em" />}
            <Box>{isOpen ? readLessText : readMoreText}</Box>
          </HStack>
        </Link>
      )}
    </Box>
  );
};
